
import maplibregl, { Map, NavigationControl, Popup } from 'maplibre-gl';
// @ts-ignore
import Compare from '@maplibre/maplibre-gl-compare';
// @ts-ignore
// import { Protocol } from '@geomatico/maplibre-cog-protocol';
import { Protocol } from 'pmtiles';
import { AppConfig } from './config';
import { analytics } from './firebase'; // Initialize Analytics
import { logEvent } from 'firebase/analytics';

// Define styles for the maps (empty basics to hold our custom layers)
const baseStyle: maplibregl.StyleSpecification = {
    version: 8,
    sources: {},
    layers: []
};

// State
let beforeMap: Map;
let afterMap: Map;
let slipsVisible = true;




document.addEventListener('DOMContentLoaded', () => {
    // Register custom protocol for PMTiles
    const protocol = new Protocol();
    maplibregl.addProtocol("pmtiles", protocol.tile);

    initMaps();
    setupUI();
});

function initMaps() {
    // Initialize Before Map
    beforeMap = new maplibregl.Map({
        container: 'before-map',
        style: { ...baseStyle, sources: {}, layers: [] }, // Deep copy or fresh object
        center: AppConfig.map.center,
        zoom: AppConfig.map.zoom,
        attributionControl: false, // Cleaner UI
        pitchWithRotate: false,
        touchPitch: false
        // Interactive true allows scrolling/zooming on the left map too. Sync is handled by Compare.
    });

    // Initialize After Map
    afterMap = new maplibregl.Map({
        container: 'after-map',
        style: { ...baseStyle, sources: {}, layers: [] },
        center: AppConfig.map.center,
        zoom: AppConfig.map.zoom,
        attributionControl: false,
        pitchWithRotate: false,
        touchPitch: false
    });

    // Wait for maps to load before adding layers
    Promise.all([
        new Promise<void>(resolve => beforeMap.on('load', resolve)),
        new Promise<void>(resolve => afterMap.on('load', resolve))
    ]).then(() => {
        setupLayerControls('before');
        setupLayerControls('after');
        addLayers();
        initCompare();

        // Add controls
        afterMap.addControl(new NavigationControl({ showCompass: true, showZoom: true }), 'top-left'); // Put on left to avoid right UI

        // Initial update
        updateLocationPanel(beforeMap);
    });

    // Update location panel on move
    beforeMap.on('move', () => {
        updateLocationPanel(beforeMap);
    });
}

function addLayers() {
    // 1. Add Before Layer
    addLayerWithFallback(beforeMap, AppConfig.layers.before, 'before');

    // 2. Add After Layer
    addLayerWithFallback(afterMap, AppConfig.layers.after, 'after');

    // 3. Add Area Mask (Dims outside)
    addAreaMaskLayer(beforeMap);
    addAreaMaskLayer(afterMap);

    // 4. Add Slips to Both Maps
    // addSlipsLayer(beforeMap); // Removed from before map per request
    addSlipsLayer(afterMap);

    // 4. Add Labels (Topmost)
    addLabelsLayer(beforeMap);
    addLabelsLayer(afterMap);
}
// ...
function addSlipsLayer(map: Map) {
    const slipsConfig = AppConfig.layers.slips;
    // @ts-ignore
    // @ts-ignore
    if (!map.getSource(slipsConfig.sourceId)) {
        // @ts-ignore
        if (slipsConfig.type === 'geojson') {
            map.addSource(slipsConfig.sourceId, {
                type: 'geojson',
                // @ts-ignore
                data: slipsConfig.url
            });
            // @ts-ignore
        } else if (slipsConfig.type === 'vector') {
            // Check if it's a PMTiles URL
            const isPMTiles = slipsConfig.url.startsWith('pmtiles://');
            const sourceOptions: any = {
                type: 'vector'
            };

            if (isPMTiles) {
                // For PMTiles, just pass the pmtiles:// URL as the 'url' property which maps to TileJSON usually
                // But the pmtiles protocol handles raw requests too.
                // However, MapLibre expects 'url' to point to tilejson describing the source OR use 'tiles' array.
                // Protocol usually handles tiles array.
                // BUT pmtiles protocol is smart. Let's see if we can use it as a TileJSON URL first? No.
                // Let's use 'url' property directly if maplibre supports custom protocol there. It does.
                sourceOptions.url = slipsConfig.url;
            } else {
                sourceOptions.tiles = [slipsConfig.url];
            }

            map.addSource(slipsConfig.sourceId, sourceOptions);
        }
    }

    // Add Fill Layer for Slips
    if (!map.getLayer(slipsConfig.id)) {
        const layerDef: any = {
            id: slipsConfig.id,
            source: slipsConfig.sourceId,
            type: 'fill',
            paint: {
                'fill-color': [
                    'match',
                    ['get', 'category'],
                    'Silt', '#8338EC', // Silt
                    '#FF006E' // Landslide
                ],
                'fill-opacity': 0.8
            }
        };
        // @ts-ignore
        if (slipsConfig.sourceLayer) layerDef['source-layer'] = slipsConfig.sourceLayer;

        map.addLayer(layerDef);
    }

    if (!map.getLayer(`${slipsConfig.id}-outline`)) {
        const outlineDef: any = {
            id: `${slipsConfig.id}-outline`,
            source: slipsConfig.sourceId,
            type: 'line',
            paint: {
                'line-color': [
                    'match',
                    ['get', 'category'],
                    'Silt', '#8338EC',
                    '#FF006E'
                ],
                'line-width': 2,
                'line-opacity': 1
            }
        };
        // @ts-ignore
        if (slipsConfig.sourceLayer) outlineDef['source-layer'] = slipsConfig.sourceLayer;

        map.addLayer(outlineDef);
    }
}

function addAreaMaskLayer(map: Map) {
    const config = AppConfig.layers.areaMask;
    // @ts-ignore
    if (config && !map.getSource(config.sourceId)) {
        map.addSource(config.sourceId, {
            type: 'geojson',
            // @ts-ignore
            data: config.url
        });
    }

    // @ts-ignore
    if (config && !map.getLayer(config.id)) {
        // Insert before slips if slips exist, otherwise just add (will be below labels)
        const beforeId = map.getLayer(AppConfig.layers.slips.id) ? AppConfig.layers.slips.id : undefined;

        // 1. Dimming Mask (Fill)
        map.addLayer({
            id: config.id,
            source: config.sourceId,
            type: 'fill',
            paint: {
                'fill-color': '#000000',
                'fill-opacity': 0.6 // Dim the non-analysed area
            }
        }, beforeId);

        // 2. Boundary Line (Outline)
        map.addLayer({
            id: `${config.id}-outline`,
            source: config.sourceId,
            type: 'line',
            paint: {
                'line-color': '#ffffff',
                'line-width': 1.5,
                'line-opacity': 0.8,
                'line-dasharray': [2, 1] // Subtle dashed line specifically for the boundary
            }
        }, beforeId);
    }
}

// ...

function addLabelsLayer(map: Map) {
    const config = AppConfig.layers.labels;
    // @ts-ignore
    if (!map.getSource(config.sourceId)) {
        map.addSource(config.sourceId, {
            type: 'raster',
            tiles: [config.url],
            tileSize: 256
        });
    }
    // @ts-ignore
    if (!map.getLayer(config.id)) {
        map.addLayer({
            id: config.id,
            source: config.sourceId,
            type: 'raster',
            paint: {
                'raster-opacity': 0.8 // Slightly transparent text for nice blend
            }
        });
    }
}

function addLayerWithFallback(map: Map, config: any, mapType: 'before' | 'after') {
    // Attempt Primary (now first option)
    const primary = config.options[0];

    if (!map.getSource(config.sourceId)) {
        if (primary.url === 'empty') return;

        map.addSource(config.sourceId, {
            type: primary.type,
            tiles: [primary.url],
            scheme: primary.scheme,
            tileSize: 256
        });

        // Ensure we insert underneath the slips layer if it exists
        const beforeId = map.getLayer(AppConfig.layers.slips.id) ? AppConfig.layers.slips.id : undefined;

        map.addLayer({
            id: config.id,
            type: primary.type,
            source: config.sourceId,
            paint: {}
        }, beforeId);
        updateLayerUI(mapType, primary.name, primary.date);
    }
}

// --- LAYER CONTROLS LOGIC ---

/**
 * Initializes the layer control UI structure (Pills + Menus).
 * Only run this ONCE per map.
 */
function setupLayerControls(type: 'before' | 'after') {
    const mapDiv = document.getElementById(`${type}-map`);
    if (!mapDiv) return;

    // Create container
    const control = document.createElement('div');
    control.className = 'layer-control';
    control.id = `${type}-layer-control`;

    // 1. Label Pill
    const pill = document.createElement('div');
    pill.className = 'control-pill group';
    pill.id = `${type}-pill`;
    pill.innerHTML = `
        <svg class="w-4 h-4 ml-2 mr-2 text-slate-400 group-hover:text-dragonfly-600 transition-colors" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
        <span class="pill-value" id="${type}-current-name">Loading...</span>
        <svg class="pill-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
    `;

    // 2. Date Badge
    const dateBadge = document.createElement('span');
    dateBadge.className = 'control-date';
    dateBadge.id = `${type}-current-date`;
    dateBadge.innerText = '...';

    // 3. Dropdown Menu
    const menu = document.createElement('div');
    menu.className = 'layer-menu';
    menu.id = `${type}-menu`;

    // Append standard layer options based on config
    const layerConfig = AppConfig.layers[type];
    const options = layerConfig.options;

    menu.innerHTML = options.map(opt => `
        <div class="menu-item" data-type="${type}" data-url="${opt.url}" data-scheme="${opt.scheme || 'xyz'}" data-name="${opt.name}" data-date="${opt.date}" data-attr="${opt.attribution || ''}">
            <span>${opt.name}</span>
            <span class="text-xs text-slate-400 ml-2">${opt.date}</span>
        </div>
    `).join('');

    // Assemble
    control.appendChild(menu); // Menu is absolute relative to control
    control.appendChild(pill);
    control.appendChild(dateBadge); // Date attached below/beside

    mapDiv.appendChild(control);

    // Event Listeners (Bound once, never destroyed)
    pill.addEventListener('click', (e) => {
        e.stopPropagation();
        toggleMenu(type);
    });

    menu.querySelectorAll('.menu-item').forEach(item => {
        item.addEventListener('click', (e) => {
            e.stopPropagation();
            const target = e.currentTarget as HTMLElement;
            const url = target.dataset.url!;
            const scheme = target.dataset.scheme!;
            const name = target.dataset.name!;
            const date = target.dataset.date!;
            const attr = target.dataset.attr!;

            // Perform update
            switchLayer(type as 'before' | 'after', url, scheme, name, date, attr);

            // Close menu
            closeAllMenus();
        });
    });
}

/**
 * Updates just the text/state of the UI, never the DOM structure.
 */
function updateLayerUI(type: 'before' | 'after', name: string, date: string) {
    const nameEl = document.getElementById(`${type}-current-name`);
    const dateEl = document.getElementById(`${type}-current-date`);

    if (nameEl) nameEl.innerText = name;
    if (dateEl) dateEl.innerText = date;

    // Highlight active item
    const menu = document.getElementById(`${type}-menu`);
    if (menu) {
        menu.querySelectorAll('.menu-item').forEach(item => {
            const itemEl = item as HTMLElement;
            if (itemEl.dataset.name === name) {
                itemEl.classList.add('active');
            } else {
                itemEl.classList.remove('active');
            }
        });
    }
}

function toggleMenu(type: string) {
    const menu = document.getElementById(`${type}-menu`);
    const wasOpen = menu?.classList.contains('open');

    closeAllMenus(); // Close others first

    if (menu && !wasOpen) {
        menu.classList.add('open');
    }
}

function closeAllMenus() {
    document.querySelectorAll('.layer-menu').forEach(el => el.classList.remove('open'));
}

// Global click to close
window.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (!target.closest('.layer-control')) {
        closeAllMenus();
    }
});

function switchLayer(type: 'before' | 'after', url: string, scheme: string, name: string, date: string, attribution: string) {
    const map = type === 'before' ? beforeMap : afterMap;
    const config = AppConfig.layers[type];

    // Swap Source Logic
    if (map.getLayer(config.id)) map.removeLayer(config.id);
    if (map.getSource(config.sourceId)) map.removeSource(config.sourceId);

    // Handle Empty
    if (url === 'empty') {
        updateLayerUI(type, name, date);
        return;
    }

    map.addSource(config.sourceId, {
        type: 'raster',
        tiles: [url],
        scheme: scheme as 'xyz' | 'tms',
        tileSize: 256
    });

    // Ensure we insert underneath the slips layer if it exists
    const beforeId = map.getLayer(AppConfig.layers.slips.id) ? AppConfig.layers.slips.id : undefined;

    map.addLayer({
        id: config.id,
        type: 'raster',
        source: config.sourceId,
        paint: {}
    }, beforeId);

    // Update UI text (Does NOT rebuild DOM)
    updateLayerUI(type, name, date);
}

// Remove the old global window functions if they exist
// @ts-ignore
if (window.toggleSelector) delete window.toggleSelector;
// @ts-ignore
if (window.selectLayer) delete window.selectLayer;

function initCompare() {
    const container = document.getElementById('comparison-container');
    if (container) {
        const compare = new Compare(beforeMap, afterMap, container, {
            // mousemove: true // Update on mousemove? usually drag is better
        });

        // Set slider to 1/3 position
        setTimeout(() => {
            compare.setSlider(Math.round(window.innerWidth * 0.33));
        }, 100);

        // Append labels to the slider handle so they move with it
        setTimeout(() => {
            const slider = container.querySelector('.mapboxgl-compare') || container.querySelector('.maplibregl-compare');
            if (slider) {
                // Analytics: Slider Interaction
                const logSlider = () => {
                    logEvent(analytics, 'slider_interaction', {
                        content_type: 'map_compare'
                    });
                };
                slider.addEventListener('mouseup', logSlider);
                slider.addEventListener('touchend', logSlider);

                // Before Label (Left of slider)
                // We use absolute positioning relative to the slider handle
                const beforeLabel = document.createElement('div');
                beforeLabel.className = 'absolute top-6 right-full mr-4 z-30 pointer-events-none bg-black/30 backdrop-blur-sm text-white/90 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest shadow-sm border border-white/20 whitespace-nowrap flex items-center justify-center';
                beforeLabel.innerText = 'Before';
                slider.appendChild(beforeLabel);

                // After Label (Right of slider)
                const afterLabel = document.createElement('div');
                afterLabel.className = 'absolute top-6 left-full ml-4 z-30 pointer-events-none bg-black/30 backdrop-blur-sm text-white/90 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest shadow-sm border border-white/20 whitespace-nowrap flex items-center justify-center';
                afterLabel.innerText = 'After';
                slider.appendChild(afterLabel);
            }
        }, 150);
    }
}

function setupUI() {
    // Toggle Slips
    const toggleBtn = document.getElementById('toggle-slips');
    const thumb = toggleBtn?.querySelector('.switch-thumb');

    toggleBtn?.addEventListener('click', () => {
        slipsVisible = !slipsVisible;
        const visibility = slipsVisible ? 'visible' : 'none';

        // Toggle layers on BOTH maps
        [beforeMap, afterMap].forEach(map => {
            if (map.getLayer(AppConfig.layers.slips.id)) {
                map.setLayoutProperty(AppConfig.layers.slips.id, 'visibility', visibility);
                map.setLayoutProperty(`${AppConfig.layers.slips.id}-outline`, 'visibility', visibility);
            }
        });

        // Update UI
        if (slipsVisible) {
            toggleBtn.classList.remove('switch-off');
            toggleBtn.classList.add('switch-on');
            thumb?.classList.remove('switch-thumb-off');
            thumb?.classList.add('switch-thumb-on');
            toggleBtn.setAttribute('aria-checked', 'true');
        } else {
            toggleBtn.classList.remove('switch-on');
            toggleBtn.classList.add('switch-off');
            thumb?.classList.remove('switch-thumb-on');
            thumb?.classList.add('switch-thumb-off');
            toggleBtn.setAttribute('aria-checked', 'false');
        }

        // Analytics: Toggle Layer
        logEvent(analytics, 'toggle_slips', {
            visible: slipsVisible,
            content_type: 'layer_toggle'
        });
    });

    // Analytics: Download GPKG
    const downloadBtn = document.getElementById('download-data-btn');
    if (downloadBtn) {
        downloadBtn.addEventListener('click', () => {
            logEvent(analytics, 'file_download', {
                file_name: '2026-January_East-Cape_Landslide-and-silt-polygons.gpkg',
                file_extension: 'gpkg'
            });
        });
    }

    // Minimize UI Logic
    const minBtn = document.getElementById('minimize-btn');
    const uiPanel = document.getElementById('main-ui-panel');
    if (minBtn && uiPanel) {
        minBtn.addEventListener('click', () => {
            const isMinimized = uiPanel.classList.toggle('minimized');
            // Update Icon (Minus -> Plus)
            minBtn.innerHTML = isMinimized
                ? `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4"><path fill-rule="evenodd" d="M10 3a.75.75 0 01.75.75v5.5h5.5a.75.75 0 010 1.5h-5.5v5.5a.75.75 0 01-1.5 0v-5.5h-5.5a.75.75 0 010-1.5h5.5v-5.5A.75.75 0 0110 3z" clip-rule="evenodd" /></svg>`
                : `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4"><path fill-rule="evenodd" d="M4 10a.75.75 0 01.75-.75h10.5a.75.75 0 010 1.5H4.75A.75.75 0 014 10z" clip-rule="evenodd" /></svg>`;

            minBtn.title = isMinimized ? "Expand" : "Minimize";
        });
    }

    // Info Modal Logic
    const modal = document.getElementById('info-modal');
    const openBtn = document.getElementById('open-info');
    const closeBtn = document.getElementById('close-modal');
    const startBtn = document.getElementById('start-exploring'); // New
    const versionEl = document.getElementById('app-version');
    const updatedEl = document.getElementById('app-updated');

    if (modal && openBtn && closeBtn) {
        const hideModalAndResize = () => {
            modal.classList.add('hidden');
            // Trigger map resize after modal closes so MapLibre recalculates tiles
            if (beforeMap) beforeMap.resize();
            if (afterMap) afterMap.resize();
        };

        openBtn.addEventListener('click', () => {
            modal.classList.remove('hidden');
        });

        closeBtn.addEventListener('click', hideModalAndResize);

        if (startBtn) {
            startBtn.addEventListener('click', hideModalAndResize);
        }

        // Close on backdrop click
        modal.addEventListener('click', (e) => {
            if (e.target === modal) {
                hideModalAndResize();
            }
        });
    }

    // Set Version Info & Updated Time
    if (versionEl || updatedEl) {
        // @ts-ignore
        import('./version').then(m => {
            if (versionEl) versionEl.innerText = `v${m.APP_VERSION}`;
            if (updatedEl && m.APP_BUILD_TIME) updatedEl.innerText = m.APP_BUILD_TIME;

            // Analytics: App Version
            logEvent(analytics, 'app_info', {
                version: m.APP_VERSION,
                build_time: m.APP_BUILD_TIME || 'unknown'
            });
        }).catch(() => {
            if (versionEl) versionEl.innerText = 'v1.0.0';
        });
    }

    // Reset View
    document.getElementById('reset-view')?.addEventListener('click', () => {
        beforeMap.flyTo({
            center: AppConfig.map.center,
            zoom: AppConfig.map.zoom
        });
        // After map syncs automatically via Compare usually, but good to be safe or just fly one
    });
}



function updateLocationPanel(map: Map) {
    const zoom = map.getZoom().toFixed(1);
    const center = map.getCenter();
    const lat = center.lat.toFixed(4);
    const lng = center.lng.toFixed(4);

    const zoomEl = document.getElementById('map-zoom');
    const latEl = document.getElementById('map-lat');
    const lngEl = document.getElementById('map-lng');

    if (zoomEl) zoomEl.innerText = zoom;
    if (latEl) latEl.innerText = lat;
    if (lngEl) lngEl.innerText = lng;
}
